import { createContext, useContext, useState } from "react";

/* ---------- Translations (public pages) ---------- */
const translations = {
  fr: {
    brand: {
      tagline: "Enregistre. Transcrit. Résume.",
    },
    nav: {
      features: "Fonctionnalités",
      pricing: "Tarifs",
      about: "À propos",
      contact: "Contact",
      login: "Se connecter",
      signup: "Essayer gratuitement",
    },
    hero: {
      t1: "Transformez vos réunions en",
      accent: "comptes-rendus intelligents",
      p1: "AfriMeet est la plateforme SaaS dédiée aux entreprises, start-ups, ONG et administrations africaines.",
      p2: "Enregistrez, transcrivez et résumez vos réunions automatiquement. Disponible en français et en anglais.",
      cta: "Commencer gratuitement",
      points: [
        { b: "Résumé", s: "en moins de 30 secondes" },
        { b: "Détection auto.", s: "des décisions et actions" },
        { b: "Données sécurisées", s: "hébergées en Afrique" },
      ],
    },
    how: {
      title: "Comment ça marche ?",
      steps: [
        { t: "Créez la réunion", d: "Programmez ou rejoignez une réunion en quelques secondes." },
        { t: "Enregistrez l'audio", d: "Cliquez sur « Enregistrer ». AfriMeet écoute pour vous." },
        { t: "Recevez votre compte-rendu", d: "Résumé IA prêt à partager en quelques secondes." },
      ],
      cta: "Commencer gratuitement",
    },
    features: {
      title: "Tout ce qu'il vous faut pour des réunions productives",
      items: [
        { t: "Transcription automatique", d: "Conversion audio en texte précise et rapide." },
        { t: "Résumé intelligent", d: "Résumés clairs, synthétiques et structurés par l'IA." },
        { t: "Décisions & responsabilités", d: "Identifie automatiquement les décisions, responsables et échéances." },
        { t: "Actions assignées", d: "Attribue les tâches et suit leur avancement." },
        { t: "Export simplifié", d: "Exporte vos comptes-rendus en PDF en un clic." },
        { t: "Recherche intelligente", d: "Trouvez une information en quelques secondes." },
      ],
      langLabel: "Langues supportées :",
      french: "Français",
      english: "Anglais",
    },
    showcase: {
      annotations: [
        { t: "Transcription en temps réel", d: "Suivez vos échanges en direct avec une grande précision." },
        { t: "Recherche intelligente", d: "Posez une question, trouvez instantanément ce qu'il faut." },
        { t: "Décisions détectées", d: "Toutes les décisions importantes identifiées." },
        { t: "Actions assignées", d: "Attribution automatique aux bonnes personnes." },
      ],
      card: {
        title: "Réunion Comité de Direction",
        tabs: ["Résumé", "Transcription", "Décisions", "Actions", "Fichiers"],
        execTitle: "Résumé exécutif",
        execText: "La réunion a porté sur l'analyse des résultats du trimestre, la validation du budget marketing et le lancement du nouveau projet Alpha.",
        decisionsTitle: "Décisions clés",
        decisions: [
          "Validation du budget marketing Q2",
          "Lancement du projet Alpha le 16 juin",
          "Recrutement d'un chef de projet",
        ],
        actionsTitle: "Actions assignées",
        actions: [
          { n: "Awa K.", t: "Préparer le rapport détaillé", d: "15 mai 2026" },
          { n: "Yann B.", t: "Mettre à jour le budget", d: "19 mai 2026" },
          { n: "Sandra M.", t: "Planifier la réunion de kick-off", d: "20 mai 2026" },
        ],
      },
    },
    preview: {
      title: "Tableau de bord",
      user: "Star Africa",
      sidebar: { home: "Accueil", meetings: "Réunions", team: "Équipe", settings: "Paramètres" },
      stats: [
        { v: "32", l: "Réunions" },
        { v: "18h 42m", l: "Temps total" },
        { v: "128", l: "Décisions" },
        { v: "85%", l: "Taux de précision" },
      ],
      meetingsTitle: "Mes dernières réunions",
      meetings: [
        { t: "Comité de Direction", d: "12 mai 2026 · 10h00 · 60min" },
        { t: "Point Projet Alpha", d: "11 mai 2026 · 14h00 · 45min" },
        { t: "Réunion Marketing Q2", d: "9 mai 2026 · 09h00 · 30min" },
        { t: "Revue Mensuelle Commerciale", d: "8 mai 2026 · 11h00 · 45min" },
      ],
      summaryTitle: "Résumé de la réunion",
      summaryBullets: [
        "Présentation des résultats du trimestre",
        "Validation du budget marketing",
        "Lancement du nouveau projet",
        "Points d'actions définis",
      ],
      viewSummary: "Voir le résumé",
      badges: [
        { v: "12 décisions", s: "détectées" },
        { v: "24 actions", s: "assignées" },
        { v: "Résumé", s: "prêt" },
      ],
    },
    sectors: {
      title: "Une solution adaptée à chaque organisation",
      items: [
        { t: "Banques & Assurances", d: "Améliorez le suivi des comités et la conformité." },
        { t: "Administrations", d: "Documentez, archivez et tracez toutes les décisions." },
        { t: "ONG & Associations", d: "Coordonnez vos projets et équipes efficacement." },
        { t: "Entreprises", d: "Accélérez l'exécution et la productivité." },
        { t: "Télécoms & Tech", d: "Pilotez vos projets et innovations." },
      ],
    },
    pricing: {
      title: "Des tarifs simples et transparents",
      starter: {
        tier: "Starter",
        name: "Essai gratuit",
        sub: "Pour découvrir la plateforme",
        features: ["2 réunions incluses", "30 minutes par réunion", "Transcription automatique", "Résumé intelligent"],
        cta: "Commencer l'essai gratuit",
        note: "Aucune carte bancaire requise",
      },
      business: {
        tier: "Business",
        badge: "Recommandé",
        period: "/ mois",
        sub: "Pour les équipes professionnelles",
        features: ["Réunions illimitées", "Transcription prioritaire", "Export PDF des comptes-rendus", "Actions & décisions automatiques", "Recherche intelligente", "Support prioritaire"],
        cta: "Choisir Business",
      },
    },
    testimonials: { title: "", items: [] },
    faq: {
      title: "Questions fréquentes",
      items: [
        { q: "AfriMeet est-il vraiment gratuit ?", a: "Oui, le plan Starter est gratuit avec 2 réunions incluses. Aucune carte bancaire n'est requise." },
        { q: "Quelles langues sont supportées ?", a: "AfriMeet supporte le français et l'anglais nativement, ainsi que les réunions mixtes français/anglais." },
        { q: "Mes données sont-elles sécurisées ?", a: "Toutes les données sont chiffrées en transit et au repos. Vos enregistrements ne sont jamais utilisés pour entraîner nos modèles." },
        { q: "Comment fonctionne l'abonnement Business ?", a: "L'abonnement Business coûte 35 000 FCFA pour 30 jours. Vous pouvez le renouveler à tout moment depuis votre espace Abonnement." },
        { q: "Puis-je inviter mon équipe ?", a: "Oui, votre espace de travail permet d'inviter des collaborateurs avec des rôles Admin, Manager ou Collaborateur." },
        { q: "Puis-je annuler mon abonnement à tout moment ?", a: "Oui, l'abonnement n'est pas reconduit sans votre action. Sans renouvellement, vous repassez simplement au plan gratuit." },
      ],
    },
    footer: {
      tagline: "Transformez vos réunions en comptes-rendus intelligents.",
      product: "Produit",
      company: "Entreprise",
      langs: "Langues supportées",
      rights: "© 2026 AfriMeet. Tous droits réservés.",
      legal: ["Mentions légales", "Confidentialité", "Conditions d'utilisation"],
    },
    featuresPage: {
      title: "Des fonctionnalités pensées pour vos réunions",
      sub: "De l'enregistrement audio au compte-rendu distribué par email, AfriMeet automatise chaque étape de vos réunions.",
    },
    pricingPage: {
      title: "Tarifs",
      sub: "Commencez gratuitement, passez au plan Business quand votre équipe est prête. Sans engagement.",
    },
    about: {
      title1: "À propos d'",
      intro: "AfriMeet est née d'un constat simple : chaque jour, des milliers de réunions ont lieu dans les entreprises, start-ups, ONG et administrations africaines… et la plupart des décisions prises s'évaporent faute de compte-rendu clair.",
      storyTitle: "Notre histoire",
      story: [
        "Combien de fois avez-vous quitté une réunion en vous demandant : « Qui devait faire quoi, et pour quand ? » Entre les notes éparpillées, les comptes-rendus rédigés des jours plus tard et les décisions oubliées, les organisations perdent un temps précieux et une partie de leur mémoire collective.",
        "AfriMeet résout ce problème grâce à l'intelligence artificielle. Notre plateforme enregistre vos réunions, les transcrit automatiquement en français comme en anglais, puis génère en quelques secondes un compte-rendu structuré : résumé exécutif, décisions prises, actions à réaliser avec responsables et échéances, risques identifiés et points ouverts.",
        "Conçue pour le contexte africain — réunions multilingues, connexions variables, tarification en FCFA — AfriMeet permet aux équipes de se concentrer sur l'essentiel : échanger, décider et agir. La documentation, c'est notre travail.",
      ],
      missionTitle: "Notre mission",
      missionText: "Libérer les organisations africaines de la charge de la prise de notes, en transformant automatiquement chaque réunion en un compte-rendu intelligent, fiable et actionnable — afin qu'aucune décision ne se perde plus jamais.",
      visionTitle: "Notre vision",
      visionText: "Devenir l'assistant de réunion de référence du continent africain : un outil que chaque équipe — de Dakar à Nairobi, d'Abidjan à Kigali — utilise au quotidien pour piloter ses projets avec clarté et rigueur.",
      valuesTitle: "Nos valeurs",
      values: [
        { t: "Excellence africaine", d: "Nous croyons que les meilleures solutions pour l'Afrique sont conçues en Afrique, par des équipes qui comprennent ses réalités, ses langues et ses cultures de travail." },
        { t: "Confiance & sécurité", d: "Vos réunions contiennent des informations sensibles. Nous chiffrons toutes les données et ne les utilisons jamais pour entraîner nos modèles." },
        { t: "Simplicité radicale", d: "Pas besoin d'être un expert en technologie. Créez une réunion, enregistrez, et recevez votre compte-rendu. C'est tout." },
        { t: "Accessibilité", d: "Des tarifs en FCFA pensés pour les PME, ONG et administrations africaines, avec un essai gratuit sans carte bancaire." },
      ],
      stats: [
        { v: "30 sec", l: "pour générer un résumé complet" },
        { v: "2 langues", l: "français, anglais et réunions mixtes" },
        { v: "5h+", l: "économisées par semaine et par équipe" },
        { v: "100%", l: "des décisions et actions tracées" },
      ],
      whatTitle: "Ce qu'AfriMeet fait pour vous",
      whatItems: [
        "Enregistre vos réunions directement depuis le navigateur",
        "Transcrit automatiquement en français et en anglais",
        "Génère un résumé structuré : décisions, actions, risques",
        "Assigne les actions aux bons responsables avec leurs échéances",
        "Distribue le compte-rendu PDF par email aux participants",
        "Protège vos données avec un chiffrement de bout en bout",
      ],
      ctaTitle: "Prêt à transformer vos réunions ?",
      ctaText: "Essayez AfriMeet gratuitement : 2 réunions incluses, aucune carte bancaire requise.",
      ctaBtn: "Commencer gratuitement",
    },
    contact: {
      title: "Contactez-nous",
      sub: "Une question, une démonstration, un projet d'équipe ? Notre équipe vous répond sous 24h ouvrées.",
      name: "Nom complet",
      namePh: "Aminata Diallo",
      email: "Email",
      emailPh: "vous@entreprise.com",
      subject: "Sujet",
      subjectPh: "Demande de démonstration",
      message: "Message",
      messagePh: "Décrivez votre besoin…",
      send: "Envoyer le message",
      sending: "Envoi en cours…",
      success: "Message envoyé ! Nous vous répondrons très vite.",
      error: "Échec de l'envoi. Réessayez.",
      infoTitle: "Parlons de vos réunions",
      infoText: "Que vous soyez une PME, une ONG, une banque ou une administration, nous vous aidons à mettre en place des comptes-rendus automatiques dans votre organisation.",
      points: ["Réponse sous 24h ouvrées", "Démonstration personnalisée gratuite", "Accompagnement au déploiement en équipe"],
    },
  },

  en: {
    brand: {
      tagline: "Record. Transcribe. Summarize.",
    },
    nav: {
      features: "Features",
      pricing: "Pricing",
      about: "About",
      contact: "Contact",
      login: "Log in",
      signup: "Try for free",
    },
    hero: {
      t1: "Turn your meetings into",
      accent: "intelligent minutes",
      p1: "AfriMeet is the SaaS platform built for African businesses, start-ups, NGOs and public administrations.",
      p2: "Record, transcribe and summarize your meetings automatically. Available in French and English.",
      cta: "Start for free",
      points: [
        { b: "Summary", s: "in under 30 seconds" },
        { b: "Auto detection", s: "of decisions and action items" },
        { b: "Secure data", s: "hosted in Africa" },
      ],
    },
    how: {
      title: "How does it work?",
      steps: [
        { t: "Create the meeting", d: "Schedule or join a meeting in seconds." },
        { t: "Record the audio", d: "Hit “Record”. AfriMeet listens for you." },
        { t: "Get your minutes", d: "AI summary ready to share within seconds." },
      ],
      cta: "Start for free",
    },
    features: {
      title: "Everything you need for productive meetings",
      items: [
        { t: "Automatic transcription", d: "Fast and accurate audio-to-text conversion." },
        { t: "Smart summary", d: "Clear, concise, AI-structured summaries." },
        { t: "Decisions & owners", d: "Automatically identifies decisions, owners and deadlines." },
        { t: "Assigned actions", d: "Assigns tasks and tracks their progress." },
        { t: "Easy export", d: "Export your minutes to PDF in one click." },
        { t: "Smart search", d: "Find any information in seconds." },
      ],
      langLabel: "Supported languages:",
      french: "French",
      english: "English",
    },
    showcase: {
      annotations: [
        { t: "Real-time transcription", d: "Follow your discussions live with high accuracy." },
        { t: "Smart search", d: "Ask a question, instantly find what you need." },
        { t: "Detected decisions", d: "Every important decision identified." },
        { t: "Assigned actions", d: "Automatically assigned to the right people." },
      ],
      card: {
        title: "Executive Committee Meeting",
        tabs: ["Summary", "Transcript", "Decisions", "Actions", "Files"],
        execTitle: "Executive summary",
        execText: "The meeting covered the analysis of quarterly results, marketing budget approval and the kickoff of the new Alpha project.",
        decisionsTitle: "Key decisions",
        decisions: [
          "Q2 marketing budget approved",
          "Project Alpha launch on June 16",
          "Hiring of a project manager",
        ],
        actionsTitle: "Assigned actions",
        actions: [
          { n: "Awa K.", t: "Prepare the detailed report", d: "May 15, 2026" },
          { n: "Yann B.", t: "Update the budget", d: "May 19, 2026" },
          { n: "Sandra M.", t: "Schedule the kick-off meeting", d: "May 20, 2026" },
        ],
      },
    },
    preview: {
      title: "Dashboard",
      user: "Star Africa",
      sidebar: { home: "Home", meetings: "Meetings", team: "Team", settings: "Settings" },
      stats: [
        { v: "32", l: "Meetings" },
        { v: "18h 42m", l: "Total time" },
        { v: "128", l: "Decisions" },
        { v: "85%", l: "Accuracy" },
      ],
      meetingsTitle: "My recent meetings",
      meetings: [
        { t: "Executive Committee", d: "May 12, 2026 · 10:00 AM · 60min" },
        { t: "Project Alpha Sync", d: "May 11, 2026 · 02:00 PM · 45min" },
        { t: "Q2 Marketing Meeting", d: "May 9, 2026 · 09:00 AM · 30min" },
        { t: "Monthly Sales Review", d: "May 8, 2026 · 11:00 AM · 45min" },
      ],
      summaryTitle: "Meeting summary",
      summaryBullets: [
        "Quarterly results presentation",
        "Marketing budget approval",
        "New project kickoff",
        "Action items defined",
      ],
      viewSummary: "View summary",
      badges: [
        { v: "12 decisions", s: "detected" },
        { v: "24 actions", s: "assigned" },
        { v: "Summary", s: "ready" },
      ],
    },
    sectors: {
      title: "A solution for every organization",
      items: [
        { t: "Banks & Insurance", d: "Improve committee follow-up and compliance." },
        { t: "Public sector", d: "Document, archive and trace every decision." },
        { t: "NGOs & Associations", d: "Coordinate your projects and teams efficiently." },
        { t: "Businesses", d: "Accelerate execution and productivity." },
        { t: "Telecom & Tech", d: "Drive your projects and innovations." },
      ],
    },
    pricing: {
      title: "Simple, transparent pricing",
      starter: {
        tier: "Starter",
        name: "Free trial",
        sub: "To discover the platform",
        features: ["2 meetings included", "30 minutes per meeting", "Automatic transcription", "Smart AI summary"],
        cta: "Start the free trial",
        note: "No credit card required",
      },
      business: {
        tier: "Business",
        badge: "Recommended",
        period: "/ month",
        sub: "For professional teams",
        features: ["Unlimited meetings", "Priority transcription", "PDF export of minutes", "Automatic actions & decisions", "Smart search", "Priority support"],
        cta: "Choose Business",
      },
    },
    testimonials: { title: "", items: [] },
    faq: {
      title: "Frequently asked questions",
      items: [
        { q: "Is AfriMeet really free?", a: "Yes, the Starter plan is free with 2 meetings included. No credit card required." },
        { q: "Which languages are supported?", a: "AfriMeet natively supports French and English, as well as mixed French/English meetings." },
        { q: "Is my data secure?", a: "All data is encrypted in transit and at rest. Your recordings are never used to train our models." },
        { q: "How does the Business subscription work?", a: "The Business plan costs 35,000 FCFA for 30 days. You can renew it anytime from your Subscription page." },
        { q: "Can I invite my team?", a: "Yes, your workspace lets you invite collaborators with Admin, Manager or Collaborator roles." },
        { q: "Can I cancel anytime?", a: "Yes, the subscription never renews without your action. Without renewal, you simply go back to the free plan." },
      ],
    },
    footer: {
      tagline: "Turn your meetings into intelligent minutes.",
      product: "Product",
      company: "Company",
      langs: "Supported languages",
      rights: "© 2026 AfriMeet. All rights reserved.",
      legal: ["Legal notice", "Privacy", "Terms of use"],
    },
    featuresPage: {
      title: "Features designed for your meetings",
      sub: "From audio recording to minutes delivered by email, AfriMeet automates every step of your meetings.",
    },
    pricingPage: {
      title: "Pricing",
      sub: "Start for free, upgrade to Business when your team is ready. No commitment.",
    },
    about: {
      title1: "About ",
      intro: "AfriMeet was born from a simple observation: every day, thousands of meetings take place in African businesses, start-ups, NGOs and administrations… and most decisions evaporate for lack of clear minutes.",
      storyTitle: "Our story",
      story: [
        "How many times have you left a meeting wondering: “Who was supposed to do what, and by when?” Between scattered notes, minutes written days later and forgotten decisions, organizations lose precious time and part of their collective memory.",
        "AfriMeet solves this with artificial intelligence. Our platform records your meetings, transcribes them automatically in French and English, then generates structured minutes in seconds: executive summary, decisions, action items with owners and deadlines, identified risks and open points.",
        "Designed for the African context — multilingual meetings, variable connectivity, FCFA pricing — AfriMeet lets teams focus on what matters: discussing, deciding and acting. Documentation is our job.",
      ],
      missionTitle: "Our mission",
      missionText: "Free African organizations from the burden of note-taking by automatically turning every meeting into intelligent, reliable and actionable minutes — so that no decision is ever lost again.",
      visionTitle: "Our vision",
      visionText: "Become the reference meeting assistant of the African continent: a tool every team — from Dakar to Nairobi, Abidjan to Kigali — uses daily to run projects with clarity and rigor.",
      valuesTitle: "Our values",
      values: [
        { t: "African excellence", d: "We believe the best solutions for Africa are built in Africa, by teams who understand its realities, languages and work cultures." },
        { t: "Trust & security", d: "Your meetings contain sensitive information. We encrypt all data and never use it to train our models." },
        { t: "Radical simplicity", d: "No need to be a tech expert. Create a meeting, record, and receive your minutes. That's it." },
        { t: "Accessibility", d: "FCFA pricing designed for African SMEs, NGOs and administrations, with a free trial and no credit card." },
      ],
      stats: [
        { v: "30 sec", l: "to generate a full summary" },
        { v: "2 languages", l: "French, English and mixed meetings" },
        { v: "5h+", l: "saved per week per team" },
        { v: "100%", l: "of decisions and actions tracked" },
      ],
      whatTitle: "What AfriMeet does for you",
      whatItems: [
        "Records your meetings right from the browser",
        "Transcribes automatically in French and English",
        "Generates structured minutes: decisions, actions, risks",
        "Assigns action items to the right owners with deadlines",
        "Distributes the PDF minutes by email to participants",
        "Protects your data with end-to-end encryption",
      ],
      ctaTitle: "Ready to transform your meetings?",
      ctaText: "Try AfriMeet for free: 2 meetings included, no credit card required.",
      ctaBtn: "Start for free",
    },
    contact: {
      title: "Contact us",
      sub: "A question, a demo, a team project? Our team replies within 24 business hours.",
      name: "Full name",
      namePh: "Aminata Diallo",
      email: "Email",
      emailPh: "you@company.com",
      subject: "Subject",
      subjectPh: "Demo request",
      message: "Message",
      messagePh: "Describe your need…",
      send: "Send message",
      sending: "Sending…",
      success: "Message sent! We'll get back to you very soon.",
      error: "Sending failed. Please try again.",
      infoTitle: "Let's talk about your meetings",
      infoText: "Whether you are an SME, an NGO, a bank or a public administration, we help you roll out automatic meeting minutes in your organization.",
      points: ["Reply within 24 business hours", "Free personalized demo", "Team onboarding support"],
    },
  },
};

/* ---------- Context ---------- */
const LangContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("afrimeet_lang") || "fr");
  const setLang = (l) => {
    localStorage.setItem("afrimeet_lang", l);
    setLangState(l);
  };
  return (
    <LangContext.Provider value={{ lang, setLang, t: translations[lang] }}>
      {children}
    </LangContext.Provider>
  );
}

export const useLang = () => useContext(LangContext);

/* ---------- FR/EN toggle ---------- */
export function LanguageToggle({ className = "" }) {
  const { lang, setLang } = useLang();
  return (
    <div className={`inline-flex items-center rounded-md border border-af overflow-hidden text-xs font-semibold ${className}`} data-testid="language-toggle">
      <button
        onClick={() => setLang("fr")}
        data-testid="lang-fr-btn"
        className={`px-2.5 py-1.5 transition-colors ${lang === "fr" ? "bg-terracotta text-white" : "bg-transparent text-af-secondary hover:text-terracotta"}`}
      >
        FR
      </button>
      <button
        onClick={() => setLang("en")}
        data-testid="lang-en-btn"
        className={`px-2.5 py-1.5 transition-colors ${lang === "en" ? "bg-terracotta text-white" : "bg-transparent text-af-secondary hover:text-terracotta"}`}
      >
        EN
      </button>
    </div>
  );
}
